import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { selectFolderItems } from "../../../common/Sidebar/redux/selectors";
import {
  clearRagObjects,
  toggleAccordion,
} from "../../../common/Sidebar/redux/slice";
import { CLASSIFICATION_INITIAL_VALUE } from "../../../features/Classification/constants";
import {
  createClassifierAction,
  deleteClassifierAction,
  getAllClassifiersAction,
  updateClassifierWithChildrenAction,
} from "../../../features/Classification/redux/actions/actions";
import { getAllClassifierObjectVersions } from "../../../features/Classification/redux/actions/storage-versioning-actions";
import {
  selectClassifiers,
  selectIsClassifiersLoading,
  selectSelectedClassifier,
} from "../../../features/Classification/redux/selectors";
import {
  setClassifierObjectVersionsAction,
  setClassifierResultsAction,
  setClassifiersAction,
  setSelectedClassifierAction,
  setSelectedClassifierObjectVersionAction,
} from "../../../features/Classification/redux/slice";
import type {
  ClassifierValue,
  ClassPostObjType,
} from "../../../services/api/instances/classificatorApi/types";
import { createStorageObjectApi } from "../../../services/lib/storageObjectManager";
import { getServicePagePath } from "../../../services/navigation/navigateToRoute";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { getFilteredObjects } from "../../../shared/utils/objectStorageFunctions";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { generateId } from "../../../shared/utils/generateId";
import { createNotification } from "../../../app/state/notificationSlice/slice";

export const useClassifiersPage = () => {
  const dispatch = useAppDispatch();
  const classifiers = useAppSelector(selectClassifiers);
  const selectedClassifier = useAppSelector(selectSelectedClassifier);
  const isClassifierLoading = useAppSelector(selectIsClassifiersLoading);
  const folderItems = useAppSelector(selectFolderItems);
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const storage = useMemo(
    () => createStorageObjectApi<ClassifierValue>(classifiers),
    [classifiers],
  );

  const classifiersSidebarItems = useMemo(() => {
    return storage.toMenuItems();
  }, [classifiers]);

  useEffect(() => {
    dispatch(getAllClassifiersAction())
      .unwrap()
      .then((response: StorageObject<ClassifierValue>[]) => {
        const folder = storage.findParent(id as string, response);
        if (folder) {
          dispatch(toggleAccordion(folder.id));
        }
        if (id === undefined && selectedClassifier) {
          navigate(getServicePagePath(selectedClassifier.id, "classify"));
          if (selectedClassifier?.value !== null) {
            dispatch(setSelectedClassifierAction(selectedClassifier));
            dispatch(
              getAllClassifierObjectVersions(selectedClassifier.id),
            ).unwrap();
          }
        } else {
          const activeClassifierById = storage.findById(id as string, response);
          const activeClassifier = activeClassifierById || response[0];
          if (!activeClassifier) {
            navigate(getServicePagePath(null, "classify"));
            return;
          }
          const activeClassifierValue: ClassPostObjType =
            activeClassifier?.value
              ? (activeClassifier?.value?.classifier_object as ClassPostObjType)
              : (CLASSIFICATION_INITIAL_VALUE as ClassPostObjType);
          const nextClassifierValue = {
            ...activeClassifierValue,
            labels: activeClassifierValue?.labels.map((item) => {
              return {
                ...item,
                id: generateId(),
              };
            }),
          };
          const updatedClassifier = {
            ...activeClassifier,
            value: {
              ...activeClassifier.value,
              classifier_object: nextClassifierValue,
            },
          };
          dispatch(setSelectedClassifierAction(updatedClassifier));
          dispatch(
            getAllClassifierObjectVersions(updatedClassifier.id),
          ).unwrap();
          navigate(getServicePagePath(updatedClassifier.id, "classify"));
        }
      });
  }, []);

  const addClassifier = () => {
    dispatch(clearRagObjects());
    const id = selectedClassifier?.id;
    const folder = storage.findParent(id as string);
    switch (true) {
      case selectedClassifier?.value === null || folder !== null: {
        const folderId = folder?.id ? folder?.id : id;
        dispatch(createClassifierAction({ classifierType: "copy" }))
          .unwrap()
          .then((response: StorageObject<ClassifierValue>) => {
            dispatch(
              updateClassifierWithChildrenAction({
                childId: response.id,
                parentId: folderId as string,
              }),
            );
            const newClassifiers = storage.addItem(response, folderId);
            dispatch(setClassifiersAction(newClassifiers));
            dispatch(setSelectedClassifierAction(response));
            dispatch(setClassifierResultsAction(null));
            dispatch(setClassifierObjectVersionsAction([]));
            dispatch(setSelectedClassifierObjectVersionAction(null));
            navigate(getServicePagePath(response.id, "classify"));
            if (!folderItems.includes(folderId as string)) {
              dispatch(toggleAccordion(id as string));
            }
          });
        return;
      }
      case folder === null: {
        dispatch(createClassifierAction({ classifierType: "copy" }))
          .unwrap()
          .then((response: StorageObject<ClassifierValue>) => {
            const newClassifier = [...classifiers, response];
            dispatch(setClassifiersAction(newClassifier));
            dispatch(setSelectedClassifierAction(response));
            dispatch(setClassifierResultsAction(null));
            dispatch(setClassifierObjectVersionsAction([]));
            dispatch(setSelectedClassifierObjectVersionAction(null));
            navigate(getServicePagePath(response.id, "classify"));
          });
        return;
      }
      default: {
        return;
      }
    }
  };

  const addFolder = (name?: string) => {
    const postObj: {
      classifierType: "folder";
      name?: string;
      withoutCount?: boolean;
    } =
      name === t("common.default_folder_name")
        ? { classifierType: "folder" }
        : { classifierType: "folder", name: name, withoutCount: true };
    dispatch(clearRagObjects());
    dispatch(createClassifierAction(postObj))
      .unwrap()
      .then((response: StorageObject<ClassifierValue>) => {
        const newClassifier = { ...response, children: [] };
        const newClassifiers = [...classifiers, newClassifier];
        dispatch(
          setClassifiersAction(
            getFilteredObjects<ClassifierValue>(
              newClassifiers as StorageObject<ClassifierValue>[],
            ),
          ),
        );
        dispatch(setClassifierObjectVersionsAction([]));
        dispatch(setSelectedClassifierObjectVersionAction(null));
        navigate(getServicePagePath(response.id, "classify"));
      });
  };

  const copyObject = () => {
    if (selectedClassifier?.value === null) {
      dispatch(createNotification("warning", "Нельзя скопировать Папку"));
    }
    dispatch(createClassifierAction({ classifierType: "copy" }))
      .unwrap()
      .then(() => {
        dispatch(getAllClassifiersAction())
          .unwrap()
          .then((response: StorageObject<ClassifierValue>[]) => {
            dispatch(
              setSelectedClassifierAction(
                response.at(-1) as StorageObject<ClassifierValue>,
              ),
            );
            dispatch(setClassifiersAction(response));
            navigate(
              getServicePagePath(
                (response.at(-1) as StorageObject<ClassifierValue>).id,
                "classify",
              ),
            );
            dispatch(setClassifierResultsAction(null));
          });
      })
      .catch(console.error);
  };

  const handleSelectClassifier = (id: string) => {
    const classifier = storage.findById(id);
    if (selectedClassifier?.id !== id) {
      dispatch(setClassifierResultsAction(null));
    }
    dispatch(setSelectedClassifierAction(classifier));
    if (classifier?.value !== null) {
      dispatch(
        getAllClassifierObjectVersions(classifier?.id as string),
      ).unwrap();
    }
    navigate(getServicePagePath(id, "classify"));
  };

  const handleDeletedClassifier = (id: string, name: string) => {
    dispatch(deleteClassifierAction({ id, navigate }));
  };

  return {
    classifiers,
    selectedClassifier,
    isClassifierLoading,
    classifiersSidebarItems,
    addFolder,
    addClassifier,
    copyObject,
    handleSelectClassifier,
    handleDeletedClassifier,
  };
};
